// 侧边栏分区：把后端菜单按「运营者(operator) / 管理员(admin)」职责拆分，
// 并支持：隐藏个别页面(HIDDEN_LINKS) 与 将个别页面移入管理区(ADMIN_LINKS)。
//
// 关键：路由链接是确定的 —— formatRoutes 用 `@/views{path}/{component}.vue` 装载，
// 故 link = 目录path + "/" + kebab(组件名/文件名)。据此按 link 精确归类/隐藏。

import lineUtil from "@/utils/humpToLine";

// 置顶必备项（运营者最常用）——按此顺序钉在最上方，无分组标题（交易紧随首页）
export const ESSENTIAL_COMPONENTS = ["Order", "MachineUser", "Product", "Coupons", "Adverts", "DateProfit"];

// 按 link 覆盖导航文案（后端菜单名不可控时使用；值为 i18n key）
export const NAV_LABEL_OVERRIDES = {
  // 「显示定制」直达主题页
  "/myapps/theme/list": "nav.displayCustomization",
  // 客户找不到通知：报警列表与通知规则页恢复侧边栏入口，用客户能认出的名字
  "/machine/user/user-machine-alarm": "nav.alerts",
  "/system/config": "nav.notificationSettings"
};

// 菜单搜索的同义词（按 link）：客户搜「report」「tax」「refund」等找不到页面
export const NAV_SEARCH_ALIASES = {
  "/statistics/date-profit": "reports report statistics sales tax export analytics revenue profit",
  "/order/order": "transactions orders sales export refunds refunded",
  "/machine/user/user-machine-alarm": "notifications alarms faults errors",
  "/system/config": "notifications alerts email sms text",
  "/myapps/theme/list": "theme layout rows columns screen display"
};

// 按 link 重定向导航目标：后端「显示定制」指向应用启动器页（/myapps/list，
// 内含主题管理+支付方式两张卡片）→ 直达主题管理；支付方式由管理区合成入口单独承载
export const NAV_LINK_REDIRECTS = {
  "/myapps/list": "/myapps/theme/list"
};

// 导航图标覆盖（后端菜单图标大多雷同，这里按组件名给出有辨识度的图标）
export const NAV_ICONS = {
  MachineUser: "el-icon-cpu", // 设备
  Product: "el-icon-goods", // 商品
  Order: "el-icon-money", // 交易
  Coupons: "el-icon-discount", // 优惠券
  Adverts: "el-icon-video-camera", // 广告
  DateProfit: "el-icon-data-line" // 销售分析
};

// 管理员专属区域（按 path 前缀，大小写不敏感）
//  /mywallet：钱包/充值/订阅/账单（Fund Wallet）整组归管理区
const ADMIN_PATH_PREFIXES = ["/promote", "/machine/admin", "/system", "/authority", "/upgrade", "/myapps", "/mywallet"];

// 明确移入「管理」区的具体页面（原属运营区，但按业务应归管理员）
//  order-refund 退款记录 · withdrawal-record 手续费/提现记录
// （inventory-log / recharge-record / mywallet/record 已被 /logs 审计日志枢纽吸收，见 LOG_HUB_LINKS）
// wti-dashboard 不在枢纽里：那是 WTI 路由器流量的内部工具，不属于客户看的审计日志
// 虽然路径在管理区前缀下，但是运营者日常要用的页面 → 放回运营区（默认展开）
//  显示定制（主题/货道排布，如 2 行改 3 行）· 通知设置
const OPERATOR_LINKS = ["/myapps/theme/list", "/myapps/list", "/system/config"];

const ADMIN_LINKS = [
  // "/order/order-refund" 暂时隐藏，见下方 HIDDEN_LINKS
  "/order/withdrawal-record",
  // 反馈审核（运营方管理动作）
  "/helpcenter/opinions"
];

// 被「审计日志」枢纽（/logs，前端静态路由）吸收的六个旧页面。
// 与 HUB_ABSORBED 不同：枢纽本身是前端静态路由，不出现在后端菜单里，
// 因此这里无法用 grantedLinks 判断枢纽是否已授权 —— 枢纽始终可用，直接无条件隐藏旧入口，
// 段位按各自授权自行显示/隐藏（AuditLogs.vue 内的 hubGrants）。
export const LOG_HUB_LINKS = [
  "/order/recharge-record",
  "/mywallet/record",
  "/mywallet/account",
  "/mywallet/detail",
  "/machine/user/inventory-log",
  "/machine/admin/offline-record"
];

// 无条件隐藏的页面（功能被常驻组件取代）：帮助中心页→支持面板
// 报警页、通知规则页曾隐藏（改由铃铛/账户菜单进入），客户反馈找不到，已恢复入口
const HIDDEN_LINKS = [
  "/helpcenter/feedbacks",
  "/helpcenter/myfeedbacks",
  "/helpcenter/documents",
  // 续费费用记录（Fee Tracker）→ 订阅相关：入口移至设备飞出面板的续费信息处
  "/order/renewal-record",
  // 钱包充值页 → 充值按钮已并入右上角账户菜单的余额行
  "/mywallet/topup",
  // Nayax 相关入口暂时下线，等移动端推送通知一起发布时再打开（两行删掉即可恢复）：
  //  - nayax-reconcile 是 Nayax 对账页（只在测试库有，V19 迁移建的菜单）
  //  - order-refund「Refund Issued」退款记录页（测试库和生产库都有）
  "/order/nayax-reconcile",
  "/order/order-refund"
];

// 被枢纽页吸收的旧页面 → 对应枢纽页 link。
// 仅当用户菜单中确实授予了枢纽页时才隐藏旧入口 —— 子账号可能只被授予旧页面
// 而无枢纽页，此时必须保留原入口，否则该功能对其完全不可达。
const HUB_ABSORBED = {
  "/machine/user/location": "/machine/user/machine-user",
  "/machine/user/machine-group": "/machine/user/machine-user",
  "/machine/user/machine-maps": "/machine/user/machine-user",
  "/product/product-type": "/product/product",
  "/product/product-tag": "/product/product",
  "/product/product-library": "/product/product",
  "/adverts/ad-library": "/adverts/adverts",
  "/adverts/network-ads": "/adverts/adverts",
  "/adverts/view-network-ads": "/adverts/adverts",
  "/adverts/advert-group": "/adverts/adverts",
  "/adverts/machine-advert-group": "/adverts/adverts",
  "/adverts/machine-advert-label": "/adverts/adverts",
  "/statistics/machine-profit": "/statistics/date-profit",
  "/statistics/product-profit": "/statistics/date-profit",
  "/statistics/user-profit": "/statistics/date-profit",
  "/statistics/adver-statistics": "/statistics/date-profit",
  "/statistics/warn-statistics": "/statistics/date-profit",
  // 角色管理 / 功能权限 → 并入用户页的三个分段：Users | Roles | Permissions
  // （线上菜单名分别是 Role management 和 Functional authority，后者才是真正生效的权限页）
  "/authority/role": "/system/user/user",
  "/authority/authorization": "/system/user/user"
};

const startsWithPrefix = (path, prefix) => path === prefix || path.indexOf(prefix + "/") === 0 || path.indexOf(prefix) === 0;

// 取叶子的完整路由：优先 link（toNavItem 生成），回退 path（$route.path）
// 统一小写比较——后端菜单 path 大小写不定（如 /myWallet）
const linkOf = leaf => String((leaf && (leaf.link || leaf.path)) || "").toLowerCase();

// 用户后端菜单 → 已授权页面 link 集合（与 formatRoutes 同口径：path + "/" + kebab(component)，小写）
export const grantedLinks = menuList => {
  const set = new Set();
  const walk = nodes => {
    (nodes || []).forEach(n => {
      if (!n || n.hideInNav) {
        // 与 Aside 渲染口径一致：hideInNav 的节点不算作导航中「已授权」，
        // 否则枢纽被后端隐藏时，其吸收的旧页面会被隐藏而枢纽本身又不出现 → 功能不可达
        return;
      }
      if (n.children && n.children.length) {
        walk(n.children);
        return;
      }
      if (n.component && n.path) {
        set.add(String(n.path + "/" + lineUtil.toLine(n.component)).toLowerCase());
      }
    });
  };
  walk(menuList);
  return set;
};

// 是否从导航隐藏。granted 为 grantedLinks() 的结果；未提供时按保守口径（仅当有枢纽才隐藏 → 不隐藏）
export const isHiddenNav = (leaf, granted) => {
  const link = linkOf(leaf);
  if (HIDDEN_LINKS.some(l => link === l)) {
    return true;
  }
  // 审计日志枢纽始终可用（静态路由），故无条件隐藏被其吸收的旧入口
  if (LOG_HUB_LINKS.some(l => link === l)) {
    return true;
  }
  // 兜底：在线状态跟踪的后端组件名为 WTIDashboard → kebab 化成 w-t-i-dashboard，
  // 去掉连字符后再匹配，确保各种拼写变体都被隐藏（已并入审计日志）
  if (link.replace(/-/g, "").indexOf("wtidashboard") !== -1) {
    return true;
  }
  // 名称兜底：后端菜单的「在线状态跟踪」若 link 与预期不符（不同 path/组件），
  // 按显示名隐藏（已并入审计日志的在线状态段）。en/zh 皆覆盖。
  const nm = String((leaf && leaf.name) || "").trim().toLowerCase();
  if (nm === "online status tracker" || nm === "online status" || nm.indexOf("在线状态") !== -1) {
    return true;
  }
  const hub = HUB_ABSORBED[link];
  if (hub) {
    return granted ? granted.has(hub) : false;
  }
  return false;
};

// 判断单个导航项（叶子）属于 admin 还是 operator
export const classifyNav = (leaf = {}) => {
  const p = linkOf(leaf);
  // 明确移入管理区的具体页面（最高优先级）
  if (ADMIN_LINKS.some(l => p === l)) {
    return "admin";
  }
  if (OPERATOR_LINKS.some(l => p === l)) {
    return "operator";
  }
  // 经销商自有设备(/machine/user)默认运营者，不被 /machine 误判
  if (p.indexOf("/machine/user") === 0) {
    return "operator";
  }
  if (ADMIN_PATH_PREFIXES.some(prefix => startsWithPrefix(p, prefix))) {
    return "admin";
  }
  return "operator";
};

export const isEssential = component => ESSENTIAL_COMPONENTS.indexOf(component) !== -1;

export default { classifyNav, isEssential, isHiddenNav, grantedLinks, ESSENTIAL_COMPONENTS, NAV_ICONS, LOG_HUB_LINKS };
